import { useState, useEffect, useRef, useCallback } from "react";
import {
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
} from "@mui/material";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  deleteTestimonial,
  fetchTestimonials,
} from "../Redux/Action/ReviewAction";
import { PiPlayBold } from "react-icons/pi";
import { IoMdArrowRoundBack } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import { TbCloudDownload } from "react-icons/tb";
import DeleteConfirmationModal from "../Modal/DeleteConfirmationModal";
import useTitle from "../Hooks/useTitle";
import PlanLimitBanner from "../Modal/PlanLImitBanner";
import notfound from "../assets/images/no-result.png";

const YourTestimonials = () => {
  useTitle('Testimonials');
  const { t } = useTranslation("your_testimonial");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountType, setAccountType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeReview, setActiveReview] = useState();
  const [view, setView] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isPlanLimitExceeded, setIsPlanLimitExceeded] = useState(false);

  const dispatch = useDispatch();

  const fetchData = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const response = await dispatch(
          fetchTestimonials(
            page,
            accountType === "Demo" ? 0 : accountType === "Event" ? 1 : null
          )
        );

        const { data, current_page, last_page } = response.data.data;
        if (data && currentPage === 1) {
          const hasLocked = data.some(item => item.is_locked);
          if (hasLocked) {
            setIsPlanLimitExceeded(true);
          }
        }
        setReviews(data);
        setCurrentPage(current_page);
        setTotalPages(last_page);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, accountType]
  );

  // Handle filter and pagination
  const handleAccountTypeChange = (event) => {
    setAccountType(event.target.value);
    fetchData(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchData(value);
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [accountType, currentPage, fetchData]);

  const videoRef = useRef(null); // Reference to the video element
  const [isPlaying, setIsPlaying] = useState(false); // Track the play/pause state
  const [showControls, setShowControls] = useState(true); // Track the visibility of the play/pause button

  const togglePlayPause = () => {
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);

    setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  const handleMouseMove = () => {
    if (!showControls) {
      setShowControls(true);
    }
  };

  const viewReview = (review) => {
    setActiveReview(review);
    setView(true);
  };

  const handleBack = () => {
    setIsPlaying(false);
    setActiveReview();
    setView(false);
  };

  const handleDeleteRequest = (e, id) => {
    e.stopPropagation();
    setDeleteId(id);
    setOpenModal(true);
  };

  const handleConfirmDelete = async () => {
    await dispatch(deleteTestimonial(deleteId));
    if (reviews.length === 1 && currentPage === 1) {
      fetchData(currentPage);
    } else if (reviews.length > 1) {
      fetchData(currentPage);
    } else {
      fetchData(currentPage - 1);
    }
    handleBack();
    setOpenModal(false);
  };

  const BASE_URL = "https://api.happiclips.com/api"

  const handleDownload = async (activeReview) => {
    const proxyUrl = `${BASE_URL}/review-video-download?url=${encodeURIComponent(activeReview.video)}&filename=${activeReview.first_name || "video"}_testimonial`;

    const anchor = document.createElement("a");
    anchor.href = proxyUrl;
    anchor.download = "video.mp4";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  return (
    <>
      {!view ? (
        <div className="grid gap-6 p-8 mt-[76px] sm:ml-[300px] relative">

          {/* Header and Filter */}
          <div className="flex flex-col w-full md:justify-between z-[1] md:flex-row">
            <span className="font-[600] text-[26px]">{t("header")}</span>
            <div className="w-full max-w-40">
              <Select
                className="text-[14px]"
                value={accountType}
                onChange={handleAccountTypeChange}
                displayEmpty
                renderValue={(selected) => (!selected ? <p>All</p> : selected)}
                sx={{
                  border: "none",
                  borderRadius: "9px",
                  height: "50px",
                  fontSize: 14,
                  background: "white",
                  width: "100%",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& .MuiSelect-icon": { color: "#000" },
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Demo">Demo</MenuItem>
                <MenuItem value="Event">Event</MenuItem>
              </Select>
            </div>
          </div>

          {reviews.length === 0 ? (
             <div className="w-full  sm:h-[calc(100vh-350px)] flex justify-center items-center object-cover">
                <img
                  src={notfound}
                  alt="not found"
                  width={500}
                  className="mt-[70px]"
                />
              </div>
          ) : (
            <div>
              {/* Table */}
              <div
                className="bg-white p-6 rounded-2xl text-center flex flex-col justify-items-center gap-4"
                style={{ overflowX: "auto", width: "100%", overflowY: "auto", position: "relative" }}
              >
                <TableContainer component={Paper} sx={{ borderRadius: "8px", position: "relative" }}>
                  <Table sx={{ minWidth: 670 }} aria-label="simple table">
                    <TableHead sx={{ background: "#F6F6F6" }}>
                      <TableRow>
                        <TableCell>{t("guestName")}</TableCell>
                        <TableCell align="left">{t("emailId")}</TableCell>
                        <TableCell align="left">{t("review")}</TableCell>
                        <TableCell align="center">{t("menu")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading
                        ? Array.from(new Array(5)).map((_, index) => (
                          <TableRow key={index}>
                            {[...Array(4)].map((_, idx) => (
                              <TableCell key={idx}>
                                <Skeleton />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                        : reviews.map((review) => (
                          <TableRow
                            key={review.id}
                            className={review.is_locked ? "blur-sm pointer-events-none cursor-not-allowed" : "table-row-hover"}
                            onClick={() => viewReview(review)}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>
                              {review.first_name} {review.last_name}
                            </TableCell>
                            <TableCell align="left">{review.email}</TableCell>
                            <TableCell align="left">
                              {review.video && (
                                <div
                                  style={{ width: "84px", height: "45px", overflow: "hidden" }}
                                  className="review-video rounded-md relative"
                                >
                                  <span className="absolute text-white top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                    <PiPlayBold size={28} />
                                  </span>
                                  <img src={review.thumbnail} alt="thumbnail" className="w-full h-full object-cover" />
                                </div>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <div className="flex justify-center gap-2">
                                <RiDeleteBin6Line fill="#ED3939" size={18} onClick={(e) => handleDeleteRequest(e, review.id)} />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>

              {/* Pagination */}
              <div className="mx-auto my-1">
                <Pagination count={totalPages} page={currentPage} color="primary" onChange={handlePageChange} />
              </div>

              <div className={`w-full sticky bottom-4 ${isPlanLimitExceeded ? 'block' : 'hidden'}`}>
                <PlanLimitBanner />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-6 p-4 sm:p-8 mt-[76px] sm:ml-[300px]">
          <div className="flex flex-col md:flex-row md:justify-between z-[1]">
            <span className="font-semibold text-xl sm:text-2xl flex items-center gap-2 sm:gap-4">
              <IoMdArrowRoundBack
                size={24}
                className="cursor-pointer"
                onClick={() => handleBack()}
              />
              {t('testimonialDetails')}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3">
              <div className="bg-white shadow rounded-md md:rounded-2xl overflow-hidden">
                <div className="p-3 sm:p-4 border-b border-gray-300 font-semibold flex items-center justify-between">
                  <span className="text-lg">{t('basicDetails')}</span>
                  <span className="cursor-pointer">
                    <MdDelete
                      size={23}
                      color="#ED3939"
                      onClick={(e) => handleDeleteRequest(e, activeReview.id)}
                    />
                  </span>
                </div>
                <div className="p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-600">{t('name')}</span>
                      <span className="font-semibold">
                        {activeReview.first_name} {activeReview.last_name}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-600">{t('email')}</span>
                      <span className="font-semibold">{activeReview.email}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-600">{t('event')}</span>
                      <span className="font-semibold">{activeReview.event.name}</span>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-600">{t('dateNtime')}</span>
                    <span className="font-semibold">
                      {new Date(activeReview.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              {/* Uncomment if you want to include the review section
              <div className="bg-white shadow rounded-md md:rounded-2xl mt-6 overflow-hidden">
                <div className="p-4 border-b border-gray-300 font-semibold flex items-center justify-between">
                  <span className="text-lg">Customer Review</span>
                </div>
                <div className="p-4">
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-600">Review</span>
                    <span className="font-semibold">{activeReview.review}</span>
                  </div>
                </div>
              </div> */}

            </div>

            <div
              className="w-100 min-h-[550px] bg-white shadow rounded-md md:rounded-2xl relative overflow-hidden"
              onMouseMove={handleMouseMove}
            >
              <video
                src={activeReview.video}
                className="w-full h-full object-contain"
                controls
                controlsList="nodownload"
              />
              <span
                onClick={() => handleDownload(activeReview)}
                className="absolute cursor-pointer top-4 right-4 bg-white text-blue-500 shadow-lg p-2 rounded-lg"
              >
                <TbCloudDownload size={22} />
              </span>
            </div>
          </div>
        </div>

      )}
      <DeleteConfirmationModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default YourTestimonials;
